/**
 * Vehicle classifications and their map glyphs.
 *
 * Each class is drawn as an actual vehicle seen from the side, not an
 * abstract symbol: at marker size a silhouette is recognised instantly while
 * a symbol has to be decoded. The six outlines are deliberately different in
 * gross shape — van, box truck, hearse, bus, saloon, pickup — so they stay
 * apart even at 20px where fine detail disappears.
 *
 * Shapes are filled rather than stroked. A 2px outline at this size collapses
 * into a grey smudge; a solid silhouette holds its edge.
 *
 * `mark` is painted on top in the contrasting colour, which is how the two
 * van-shaped classes (ambulance and intensive care) stay distinguishable:
 * the ICU van carries a visible ECG trace along its body.
 *
 * No cross appears anywhere. The red cross is a protected emblem under the
 * Geneva Conventions and its use is restricted, and it is not the convention
 * here in any case.
 *
 * Paths are raw markup rather than components because Leaflet's `divIcon`
 * takes an HTML string, not a React element.
 */

export const VEHICLE_TYPES = [
  'ambulance',
  'icu',
  'mortuary',
  'mobile_clinic',
  'supervisor',
  'support',
] as const;

export type VehicleType = typeof VEHICLE_TYPES[number];

interface VehicleTypeMeta {
  value: string;
  label: string;
  /** Solid silhouette, drawn on a 24×24 viewBox. */
  body: string;
  /** Detail painted over the body in the contrasting colour. */
  mark?: string;
  accent: string;
}

// Ambulance: the classic boxed high-roof van — squared rear compartment, cab
// with a sloped screen, roof light bar, and a plain flank stripe.
//
// Drawn from the reference the ministry supplied, minus its red cross: that
// emblem is protected under the Geneva Conventions and its use is restricted.
// Kept deliberately square-shouldered so it stays apart from the intensive
// care unit, which is the rounder minivan with the pulse stripe.
const AMBULANCE = `
  <path d="M6.2 3.9h5.8v2.3H6.2z"/>
  <path d="M1.8 16.4V7.6a1.4 1.4 0 0 1 1.4-1.4h9.6v3.2h1.9c.5 0 1 .2 1.4.5l3.2 2.9c.4.4.7 1 .7 1.6v2z"/>
  <circle cx="6.4" cy="17.3" r="2.2"/>
  <circle cx="16.6" cy="17.3" r="2.2"/>`;
// Cab and rear windows plus the flank stripe — a plain band here, where the
// intensive-care van carries an ECG trace instead.
const AMBULANCE_MARK = `
  <path d="M14.3 10.5h1.4l2.4 2.2v.3h-3.8zM3.4 8.1h3.2v2.6H3.4z"/>
  <path d="M2.6 13.1h10.1v1.7H2.6z"/>`;

// Mobile intensive care: a rounded minivan ambulance — sloped nose, side
// windows, roof light bar — carrying an ECG trace along its flank stripe.
//
// Modelled on the reference the ministry supplied, minus the red cross it
// bore: that emblem is protected under the Geneva Conventions and its use is
// restricted. The pulse line in the stripe reads as intensive care without
// borrowing a symbol we are not entitled to, and it is what keeps this class
// apart from the plain ambulance van, whose silhouette is otherwise close.
const ICU = `
  <path d="M6.4 3.6h5.6v2.2H6.4z"/>
  <path d="M2 16.2v-6.1a2.1 2.1 0 0 1 2.1-2.1h9.5c.6 0 1.2.2 1.7.6l3.9 3a2.2 2.2 0 0 1 .9 1.7v2.9z"/>
  <circle cx="6.6" cy="17.2" r="2.2"/>
  <circle cx="16.4" cy="17.2" r="2.2"/>`;
// Windows plus the flank stripe, drawn in the contrasting colour on top.
const ICU_MARK = `
  <path d="M4.3 9.5h4v2.6h-4zM9.5 9.5h3.6v2.6H9.5zM14.6 10.1h1.3l2.1 1.6v.4h-3.4z"/>
  <path fill="none" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"
        d="M3.1 14.3h2.4l1-2 1.6 4 1.1-2.6.8 1.3h8.6"/>`;

// Hearse: long, low estate body with an extended rear compartment.
const MORTUARY = `
  <path d="M1.8 9.4h9.6V7.2A1.2 1.2 0 0 1 12.6 6h4.2a1.6 1.6 0 0 1 1.3.7l2.3 3.3v4.6H1.8z"/>
  <circle cx="6" cy="15.4" r="2.1"/>
  <circle cx="16.6" cy="15.4" r="2.1"/>`;

// Minibus clinic: full-height body the whole length, windows along the side.
const MOBILE_CLINIC = `
  <path d="M2.4 4.6h17.2a1.4 1.4 0 0 1 1.4 1.4v10.4H1V6a1.4 1.4 0 0 1 1.4-1.4z"/>
  <circle cx="6" cy="17.4" r="2.1"/>
  <circle cx="16.2" cy="17.4" r="2.1"/>`;
const MOBILE_CLINIC_MARK = `
  <path d="M3.2 7h4v3.2h-4zM8.6 7h4v3.2h-4zM14 7h4v3.2h-4z"/>`;

// Saloon: low roofline, sloped screens front and back.
const SUPERVISOR = `
  <path d="M2 13.6l1.6-4A2.4 2.4 0 0 1 5.9 8h8.9a2.4 2.4 0 0 1 1.9.9l2.9 3.6 1.4.4a1.4 1.4 0 0 1 1 1.3v1.6H2z"/>
  <circle cx="6.4" cy="16.8" r="2.2"/>
  <circle cx="16.6" cy="16.8" r="2.2"/>`;

// Pickup: cab plus an open flatbed.
const SUPPORT = `
  <path d="M1.6 8.6h7.6a1.4 1.4 0 0 1 1.1.6l2.4 3.2h7.7v3.8H1.6z"/>
  <path d="M12.4 12.4h9.4v1.2h-9.4z"/>
  <circle cx="6" cy="16.6" r="2.1"/>
  <circle cx="17" cy="16.6" r="2.1"/>`;

export const VEHICLE_TYPE_META: Record<string, VehicleTypeMeta> = {
  ambulance: {
    value: 'ambulance', label: 'سيارة إسعاف',
    body: AMBULANCE, mark: AMBULANCE_MARK, accent: '#dc2626',
  },
  icu: { value: 'icu', label: 'سيارة عناية مركزة', body: ICU, mark: ICU_MARK, accent: '#db2777' },
  mortuary: { value: 'mortuary', label: 'سيارة نقل جثامين', body: MORTUARY, accent: '#475569' },
  mobile_clinic: {
    value: 'mobile_clinic', label: 'عيادة متنقلة',
    body: MOBILE_CLINIC, mark: MOBILE_CLINIC_MARK, accent: '#7c3aed',
  },
  supervisor: { value: 'supervisor', label: 'سيارة إشراف', body: SUPERVISOR, accent: '#2563eb' },
  support: { value: 'support', label: 'سيارة دعم', body: SUPPORT, accent: '#0891b2' },
};

/** Ordered list for form dropdowns. */
export const VEHICLE_TYPE_OPTIONS = VEHICLE_TYPES.map((t) => VEHICLE_TYPE_META[t]!);

export const vehicleTypeLabel = (t?: string | null): string =>
  (t && VEHICLE_TYPE_META[t]?.label) || t || 'غير محدد';

export const vehicleTypeAccent = (t?: string | null): string =>
  (t && VEHICLE_TYPE_META[t]?.accent) || '#6b7280';

/**
 * Glyph markup for a Leaflet marker: silhouette in `fill`, detail in `markFill`.
 * Unknown classes fall back to the plain van rather than rendering nothing.
 */
export const vehicleTypeSvg = (
  t: string | null | undefined,
  fill: string,
  markFill: string,
): string => {
  const meta = (t && VEHICLE_TYPE_META[t]) || VEHICLE_TYPE_META.ambulance!;
  return `<g fill="${fill}">${meta.body}</g>`
    + (meta.mark ? `<g fill="${markFill}" stroke="${markFill}">${meta.mark}</g>` : '');
};

/**
 * Status colours, keyed to the real `vehicle_status` enum values.
 *
 * The map previously tested for 'active' and 'busy', neither of which the
 * enum contains, so every online vehicle fell through to the same blue and
 * status was invisible.
 */
export const VEHICLE_STATUS_COLORS: Record<string, string> = {
  available: '#10b981',
  on_mission: '#3b82f6',
  emergency: '#ef4444',
  maintenance: '#f59e0b',
  offline: '#6b7280',
};

export const VEHICLE_STATUS_LABELS: Record<string, string> = {
  available: 'متاحة',
  on_mission: 'في مهمة',
  emergency: 'طوارئ',
  maintenance: 'صيانة',
  offline: 'غير متصلة',
};

/** A marker's fill: grey whenever the tracker is silent, status colour otherwise. */
export const vehicleMarkerColor = (status: string, isOnline: boolean): string =>
  (isOnline && VEHICLE_STATUS_COLORS[status]) || '#6b7280';

/**
 * Inline SVG for use inside React (lists, tables) rather than Leaflet. Drawn
 * in the class accent on a light background, so the fills invert relative to
 * the map marker.
 */
export function VehicleTypeIcon({ type, className = 'w-6 h-6', color }: {
  type?: string | null;
  className?: string;
  color?: string;
}) {
  const accent = color || vehicleTypeAccent(type);
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      dangerouslySetInnerHTML={{ __html: vehicleTypeSvg(type, accent, '#ffffff') }}
    />
  );
}
